"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { useRouter } from "next/navigation";
import type { GalleryAlbum } from "@/lib/sections/gallery.type";
import type { Locale } from "@/lib/i18n";
import { getWeddingBasePath } from "@/lib/wedding-routes";

type GalleryAlbumViewerProps = {
  locale: Locale;
  sectionTitle?: string;
  album: GalleryAlbum;
};

export function GalleryAlbumViewer({ locale, sectionTitle, album }: GalleryAlbumViewerProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const router = useRouter();
  const copy =
    locale === "fr"
      ? {
          back: "Retour",
          backToGallery: "Retour à la galerie",
          gallery: "Galerie",
          previous: "Précédent",
          next: "Suivant",
        }
      : locale === "en"
        ? {
            back: "Back",
            backToGallery: "Back to gallery",
            gallery: "Gallery",
            previous: "Previous",
            next: "Next",
          }
        : {
            back: "Zurueck",
            backToGallery: "Zurueck zur Galerie",
            gallery: "Galerie",
            previous: "Vorheriges",
            next: "Naechstes",
          };

  const images = album.images;
  const activeImage = images[activeIndex] ?? images[0];

  if (!activeImage) {
    return null;
  }

  return (
    <section className="bg-[#f6f6f6] py-16">
      <div className="mx-auto max-w-6xl space-y-8 px-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => router.back()}
            className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition hover:border-amber-400 hover:text-amber-700"
          >
            {copy.back}
          </button>

          <Link
            href={`${getWeddingBasePath(locale)}#gallery`}
            className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition hover:border-amber-400 hover:text-amber-700"
          >
            {copy.backToGallery}
          </Link>
        </div>

        <header className="space-y-2 text-center">
          <p className="text-xs uppercase tracking-[0.14em] text-gray-500">{sectionTitle ?? copy.gallery}</p>
          <h1 className="font-serif text-3xl text-gray-900 md:text-4xl">{album.title}</h1>
          {album.place ? <p className="text-sm text-gray-500">{album.place}</p> : null}
          {album.description ? <p className="mx-auto max-w-2xl text-sm text-gray-600">{album.description}</p> : null}
        </header>

        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
          <div className="relative h-[70vh] min-h-[420px] w-full">
            <Image src={activeImage.src} alt={activeImage.alt ?? ""} fill priority className="object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/15" />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-4">
              <p className="font-serif text-xl text-white">{activeImage.alt ?? album.title}</p>
              <p className="rounded-full bg-black/45 px-3 py-1 text-xs text-white">
                {activeIndex + 1}/{images.length}
              </p>
            </div>
          </div>

          {images.length > 1 ? (
            <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-3">
              <button
                type="button"
                onClick={() => setActiveIndex((prev) => (prev - 1 + images.length) % images.length)}
                className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition hover:border-amber-400 hover:text-amber-700"
              >
                {copy.previous}
              </button>
              <button
                type="button"
                onClick={() => setActiveIndex((prev) => (prev + 1) % images.length)}
                className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition hover:border-amber-400 hover:text-amber-700"
              >
                {copy.next}
              </button>
            </div>
          ) : null}

          <div className="grid grid-cols-4 gap-2 border-t border-gray-100 p-4 sm:grid-cols-6 md:grid-cols-8">
            {images.map((img, index) => (
              <button
                key={`${img.src}-${index}`}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`relative aspect-square overflow-hidden rounded-md border transition ${
                  index === activeIndex ? "border-amber-500" : "border-transparent hover:border-amber-300"
                }`}
              >
                <Image src={img.src} alt={img.alt ?? ""} fill className="object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
